import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const _frontmatter = {
  "title": "Vulnerability Disclosure Policy",
  "layout": "content",
  "includeToc": true,
  "permalink": "/vdp/"
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "Vulnerability-Disclosure-Policy",
      "style": {
        "position": "relative"
      }
    }}>{`Vulnerability Disclosure Policy`}</h1>
    <p><a parentName="p" {...{
        "href": "https://bugcrowd.com/onrr-vdp"
      }}>{`Report any vulnerabilities to DOI ONRR systems`}</a></p>
    <h2 {...{
      "id": "Introduction",
      "style": {
        "position": "relative"
      }
    }}>{`Introduction`}</h2>
    <p>{`The Department of the Interior (DOI) Office of Natural Resources Revenue (ONRR) is committed to ensuring the security of the American public by protecting their information. This policy is intended to give security researchers clear guidelines for conducting vulnerability discovery activities and to convey our preferences in how to submit discovered vulnerabilities to us.`}</p>
    <br />
    <p>{`This policy describes `}<strong parentName="p">{`what systems and types of research`}</strong>{` are covered under this policy, `}<strong parentName="p">{`how to send us`}</strong>{` vulnerability reports, and `}<strong parentName="p">{`how long`}</strong>{` we ask security researchers to wait before publicly disclosing vulnerabilities.`}</p>
    <br />
    <p>{`We encourage you to contact us to report potential vulnerabilities in our systems.`}</p>
    <h2 {...{
      "id": "Authorization",
      "style": {
        "position": "relative"
      }
    }}>{`Authorization`}</h2>
    <p><strong parentName="p">{`If you make a good faith effort to comply with this policy during your security research, we will consider your research to be authorized, we will work with you to understand and resolve the issue quickly, and DOI ONRR will not recommend or pursue legal action related to your research. Should legal action be initiated by a third party against you for activities that were conducted in accordance with this policy, we will make this authorization known.`}</strong></p>
    <h2 {...{
      "id": "Guidelines",
      "style": {
        "position": "relative"
      }
    }}>{`Guidelines`}</h2>
    <p>{`Under this policy, “research” means activities in which you:`}</p>
    <ul>
      <li parentName="ul">{`Notify us as soon as possible after you discover a real or potential security issue.`}</li>
      <li parentName="ul">{`Make every effort to avoid privacy violations, degradation of user experience, disruption to production systems, and destruction or manipulation of data.`}</li>
      <li parentName="ul">{`Only use exploits to the extent necessary to confirm a vulnerability’s presence. Do not use an exploit to compromise or exfiltrate data, establish command line access and/or persistence, or use the exploit to pivot to other systems.
*Provide us a reasonable amount of time to resolve the issue before you disclose it publicly.`}</li>
      <li parentName="ul">{`Do not submit a high volume of low-quality reports.`}</li>
    </ul>
    <p>{`Once you’ve established that a vulnerability exists or encounter any sensitive data (including personally identifiable information, financial information, or proprietary information or trade secrets of any party), `}<strong parentName="p">{`you must stop your test, notify us immediately, and not disclose this data to anyone else.`}</strong></p>
    <h2 {...{
      "id": "Test-methods",
      "style": {
        "position": "relative"
      }
    }}>{`Test methods`}</h2>
    <p>{`The following test methods are not authorized:`}</p>
    <ul>
      <li parentName="ul">{`Network denial of service (DoS or DDoS) tests or other tests that impair access to or damage a system or data`}</li>
      <li parentName="ul">{`Physical testing (e.g. office access, open doors, tailgating), social engineering (e.g. phishing, vishing), or any other non-technical vulnerability testing`}</li>
      <li parentName="ul">{`Full red-team penetration testing that involves unauthorized access to our servers`}</li>
    </ul>
    <h2 {...{
      "id": "Scope",
      "style": {
        "position": "relative"
      }
    }}>{`Scope`}</h2>
    <p>{`This policy applies to the following systems and services:`}</p>
    <ul>
      <li parentName="ul">{`*.onrr.gov`}</li>
      <li parentName="ul">{`revenuedata.doi.gov`}</li>
    </ul>
    <p><strong parentName="p">{`Any service not expressly listed above, such as any connected services, are excluded from scope`}</strong>{` and are not authorized for testing. Additionally, vulnerabilities found in systems from our vendors fall outside of this policy’s scope and should be reported directly to the vendor according to their Disclosure Policy (if any). If you aren’t sure whether a system is in scope or not, `}<a parentName="p" {...{
        "href": "mailto:EnterpriseITServiceDesk@bsee.gov"
      }}>{`contact us`}</a>{` before starting your research (or at the security contact for the system’s domain name listed in the `}<a parentName="p" {...{
        "href": "https://domains.dotgov.gov/dotgov-web/registration/whois.xhtml"
      }}>{`.gov WHOIS`}</a>{`).`}</p>
    <br />
    <p>{`Though we develop and maintain other internet-accessible systems or services, we ask that active research and testing only be conducted on the systems and services covered by the scope of this document. If there is a particular system not in scope that you think merits testing, please contact us to discuss it first. We will increase the scope of this policy over time.`}</p>
    <h2 {...{
      "id": "Reporting-a-vulnerability",
      "style": {
        "position": "relative"
      }
    }}>{`Reporting a vulnerability`}</h2>
    <p><em parentName="p">{`Information submitted under this policy will be used for defensive purposes only – to mitigate or remediate vulnerabilities. If your findings include newly discovered vulnerabilities that affect all users of a product or service and not solely DOI ONRR, we may share your report with the Cybersecurity and Infrastructure Security Agency, where it will be handled under their `}<a parentName="em" {...{
          "href": "https://www.cisa.gov/coordinated-vulnerability-disclosure-process"
        }}>{`coordinated vulnerability disclosure process`}</a>{`. We will not share your name or contact information without express permission.`}</em></p>
    <br />
    <p><strong parentName="p"><a parentName="strong" {...{
          "href": "https://bugcrowd.com/onrr-vdp"
        }}>{`Vulnerability Reports`}</a>{` can be submitted through `}<a parentName="strong" {...{
          "href": "https://bugcrowd.com/onrr-vdp"
        }}>{`bugcrowd`}</a>{`.`}</strong>{` Reports may be submitted anonymously. If you share contact information, we will acknowledge receipt of your report within 3 business days.`}</p>
    <br />
    <h3 {...{
      "id": "What-we-would-like-to-see-from-you",
      "style": {
        "position": "relative"
      }
    }}>{`What we would like to see from you`}</h3>
    <p>{`In order to help us triage and prioritize submissions, we recommend that your reports:`}</p>
    <ul>
      <li parentName="ul">{`Describe the location the vulnerability was discovered and the potential impact of exploitation.`}</li>
      <li parentName="ul">{`Offer a detailed description of the steps needed to reproduce the vulnerability (proof of concept scripts or screenshots are helpful).`}</li>
      <li parentName="ul">{`Be in English, if possible.`}</li>
    </ul>
    <h3 {...{
      "id": "What-you-can-expect-from-us",
      "style": {
        "position": "relative"
      }
    }}>{`What you can expect from us`}</h3>
    <p>{`When you choose to share your contact information with us, we commit to coordinating with you as openly and as quickly as possible.`}</p>
    <ul>
      <li parentName="ul">{`Within 3 business days, we will acknowledge that your report has been received.`}</li>
      <li parentName="ul">{`To the best of our ability, we will confirm the existence of the vulnerability to you and be as transparent as possible about what steps we are taking during the remediation process, including on issues or challenges that may delay resolution.`}</li>
      <li parentName="ul">{`DOI ONRR does not provide payment to reporters for submitting vulnerabilities.`}<ul parentName="li">
          <li parentName="ul">{`Reporters submitting vulnerabilities to DOI ONRR, in so doing, waive any claims to compensation.`}</li>
        </ul></li>
    </ul>
    <h2 {...{
      "id": "Questions",
      "style": {
        "position": "relative"
      }
    }}>{`Questions`}</h2>
    <p>{`Questions regarding this policy may be sent to `}<a parentName="p" {...{
        "href": "mailto:EnterpriseITServiceDesk@bsee.gov"
      }}>{`DOI ONRR`}</a>{`. We also invite you to contact us with suggestions for improving this policy.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      